import { useState, useEffect, useCallback } from 'react'
import { apiUrl, authFetch, setActiveTeam } from '../../lib/api'
import { useAuthStore } from '../../auth/useAuthStore'
import { CollabPanel } from '../CollabPanel/CollabPanel'

interface Team {
  id: string; name: string; slug: string; plan: string; my_role: string
  member_count: number; created_at: string
}

interface Member {
  id: string; user_id: string; name: string; email: string
  avatar_url: string | null; role: string; joined_at: string
}

export function TeamWorkspaces() {
  const [topTab, setTopTab] = useState<'team' | 'live'>('team')

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Top tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        {(['team', 'live'] as const).map(t => (
          <button key={t} onClick={() => setTopTab(t)} style={{
            flex: 1, padding: '8px 0', fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700,
            border: 'none', borderBottom: topTab === t ? '2px solid var(--accent)' : '2px solid transparent',
            background: 'none', color: topTab === t ? 'var(--accent)' : 'var(--text-2)', cursor: 'pointer',
          }}>
            {t === 'team' ? '👥 Team' : '🔴 Live Session'}
          </button>
        ))}
      </div>

      {topTab === 'live' ? (
        <CollabPanel />
      ) : (
        <TeamContent />
      )}
    </div>
  )
}

function TeamContent() {
  const [teams, setTeams] = useState<Team[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [members, setMembers] = useState<Member[]>([])
  const [showCreate, setShowCreate] = useState(false)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('member')

  const load = useCallback(() => {
    authFetch(apiUrl('/api/teams')).then(r => r.json()).then(d => setTeams(Array.isArray(d) ? d : [])).catch(() => {})
  }, [])

  useEffect(() => { load() }, [load])

  const loadTeam = async (id: string) => {
    setSelected(id)
    const res = await authFetch(apiUrl(`/api/teams/${id}`))
    const data = await res.json()
    setMembers(data.members ?? [])
  }

  const create = async () => {
    if (!name || !slug) return
    await authFetch(apiUrl('/api/teams'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, slug }),
    })
    setName(''); setSlug(''); setShowCreate(false); load()
  }

  const invite = async () => {
    if (!inviteEmail || !selected) return
    await authFetch(apiUrl(`/api/teams/${selected}/invite`), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
    })
    setInviteEmail(''); loadTeam(selected)
  }

  const removeMember = async (userId: string) => {
    if (!selected) return
    await authFetch(apiUrl(`/api/teams/${selected}/members/${userId}`), { method: 'DELETE' })
    loadTeam(selected)
  }

  const deleteTeam = async (id: string) => {
    await authFetch(apiUrl(`/api/teams/${id}`), { method: 'DELETE' })
    if (selected === id) { setSelected(null); setMembers([]) }
    load()
  }

  const openWorkspace = (teamId: string) => {
    // Switch to Shared panel to browse team files (Explorer is user-only)
    window.dispatchEvent(new CustomEvent('pulse:switch-view', { detail: 'shared' }))
  }

  const leaveWorkspace = () => {
    setActiveTeam(null)
    window.dispatchEvent(new CustomEvent('pulse:files-changed'))
  }

  const selectedTeam = teams.find(t => t.id === selected)
  const isTeamActive = localStorage.getItem('pulse-active-team') === selected

  return (
    <div style={{ height: '100%', display: 'flex', overflow: 'hidden' }}>
      {/* Teams list */}
      <div style={{ width: 220, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 14 }}>👥</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 11, color: 'var(--text-0)' }}>Teams</span>
          <div style={{ flex: 1 }} />
          <button onClick={() => setShowCreate(s => !s)} style={btnStyle}>+</button>
        </div>

        {showCreate && (
          <div style={{ padding: 10, borderBottom: '1px solid var(--border)', background: 'rgba(124,110,245,0.04)' }}>
            <input placeholder="Team name" value={name} onChange={e => { setName(e.target.value); setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')) }} style={inputStyle} />
            <input placeholder="Slug" value={slug} onChange={e => setSlug(e.target.value)} style={inputStyle} />
            <button onClick={create} style={{ ...btnStyle, background: 'rgba(95,212,164,0.15)', color: 'var(--accent-2)' }}>Create</button>
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto' }}>
          {teams.length === 0 && !showCreate && (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 10 }}>
              No teams yet
            </div>
          )}
          {teams.map(t => (
            <div key={t.id} onClick={() => loadTeam(t.id)} style={{
              padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)',
              background: selected === t.id ? 'rgba(124,110,245,0.08)' : 'none',
            }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: selected === t.id ? 'var(--accent)' : 'var(--text-0)' }}>{t.name}</div>
              <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                {t.member_count} members · {t.my_role}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Team detail */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {!selectedTeam ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
            Select a team to manage members
          </div>
        ) : (
          <div style={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <div>
                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 16, color: 'var(--text-0)' }}>{selectedTeam.name}</div>
                <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>/{selectedTeam.slug} · {selectedTeam.plan} plan</div>
              </div>
              <div style={{ flex: 1 }} />
              {isTeamActive ? (
                <button onClick={leaveWorkspace} style={{ ...btnStyle, background: 'rgba(240,112,112,0.15)', color: 'var(--red)' }}>Leave Workspace</button>
              ) : (
                <button onClick={() => openWorkspace(selectedTeam.id)} style={{ ...btnStyle, background: 'rgba(95,212,164,0.15)', color: 'var(--accent-2)' }}>Open Workspace</button>
              )}
              {selectedTeam.my_role === 'admin' && (
                <button onClick={() => deleteTeam(selectedTeam.id)} style={{ ...btnStyle, color: 'var(--red)' }}>Delete Team</button>
              )}
            </div>

            {/* Tab bar for enterprise teams */}
            <TeamTabs team={selectedTeam} members={members} inviteEmail={inviteEmail} setInviteEmail={setInviteEmail} inviteRole={inviteRole} setInviteRole={setInviteRole} invite={invite} removeMember={removeMember} loadTeam={loadTeam} />
          </div>
        )}
      </div>
    </div>
  )
}

const btnStyle: React.CSSProperties = {
  padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border)',
  background: 'var(--bg-2)', color: 'var(--text-1)', cursor: 'pointer',
  fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 600,
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px', marginBottom: 6, borderRadius: 6,
  border: '1px solid var(--border)', background: 'var(--bg-0)', color: 'var(--text-0)',
  fontSize: 11, fontFamily: 'var(--font-mono)', outline: 'none', width: '100%',
}

function TeamTabs({ team, members, inviteEmail, setInviteEmail, inviteRole, setInviteRole, invite, removeMember, loadTeam }: {
  team: Team; members: Member[]; inviteEmail: string; setInviteEmail: (v: string) => void
  inviteRole: string; setInviteRole: (v: string) => void; invite: () => void
  removeMember: (userId: string) => void; loadTeam: (id: string) => void
}) {
  const userPlan = useAuthStore(s => s.user?.plan)
  const isEnterprise = userPlan === 'enterprise'
  const [tab, setTab] = useState<'members' | 'sso'>('members')

  return (
    <>
      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        <button onClick={() => setTab('members')} style={{
          padding: '6px 14px', borderRadius: 6, fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 700,
          border: tab === 'members' ? '2px solid var(--accent)' : '1px solid var(--border)',
          background: tab === 'members' ? 'rgba(124,110,245,0.1)' : 'none',
          color: tab === 'members' ? 'var(--accent)' : 'var(--text-2)', cursor: 'pointer',
        }}>👥 Members ({members.length})</button>
        {isEnterprise && (
          <button onClick={() => setTab('sso')} style={{
            padding: '6px 14px', borderRadius: 6, fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 700,
            border: tab === 'sso' ? '2px solid var(--accent-2)' : '1px solid var(--border)',
            background: tab === 'sso' ? 'rgba(95,212,164,0.1)' : 'none',
            color: tab === 'sso' ? 'var(--accent-2)' : 'var(--text-2)', cursor: 'pointer',
          }}>🔐 SSO</button>
        )}
      </div>

      {tab === 'members' && (
        <>
          {/* Invite */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 20, alignItems: 'center' }}>
            <input placeholder="Email to invite" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} style={{ ...inputStyle, marginBottom: 0, flex: 1 }} />
            <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} style={{
              padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)',
              background: 'var(--bg-0)', color: 'var(--text-0)', fontSize: 10, fontFamily: 'var(--font-mono)',
            }}>
              <option value="member">Member</option>
              <option value="admin">Admin</option>
              <option value="viewer">Viewer</option>
            </select>
            <button onClick={invite} style={{ ...btnStyle, background: 'rgba(124,110,245,0.15)', color: 'var(--accent)' }}>Invite</button>
          </div>

          {/* Members list */}
          <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 11, color: 'var(--text-0)', marginBottom: 10 }}>
            Members ({members.length})
          </div>
          {members.map(m => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--bg-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>
                {m.name?.charAt(0)?.toUpperCase() ?? '?'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--text-0)' }}>{m.name}</div>
                <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{m.email}</div>
              </div>
              <span style={{ fontSize: 8, padding: '2px 6px', borderRadius: 4, fontFamily: 'var(--font-mono)', fontWeight: 700, background: m.role === 'admin' ? 'rgba(124,110,245,0.12)' : 'var(--bg-3)', color: m.role === 'admin' ? 'var(--accent)' : 'var(--text-1)' }}>{m.role.toUpperCase()}</span>
              {team.my_role === 'admin' && m.role !== 'admin' && (
                <button onClick={() => removeMember(m.user_id)} style={{ ...btnStyle, color: 'var(--red)' }}>Remove</button>
              )}
            </div>
          ))}
        </>
      )}

      {tab === 'sso' && <SSOConfig teamId={team.id} />}
    </>
  )
}

function SSOConfig({ teamId }: { teamId: string }) {
  const [config, setConfig] = useState({ provider: 'oidc', client_id: '', client_secret: '', issuer_url: '', redirect_uri: '', allowed_domain: '', enabled: false })
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    authFetch(apiUrl('/api/enterprise/sso')).then(r => r.json()).then(data => {
      if (data) setConfig(prev => ({ ...prev, ...data }))
    }).catch(() => {})
  }, [])

  const save = async () => {
    setSaving(true); setMsg('')
    try {
      const res = await authFetch(apiUrl('/api/enterprise/sso'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...config, org_id: teamId }),
      })
      if (res.ok) setMsg('✓ SSO configuration saved')
      else { const d = await res.json(); setMsg(`✗ ${d.error}`) }
    } catch (e: any) { setMsg(`✗ ${e.message}`) }
    finally { setSaving(false) }
  }

  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-1)', fontFamily: 'var(--font-mono)', marginBottom: 14, lineHeight: 1.5 }}>
        Configure SSO for your organization. Team members will log in with your corporate identity provider instead of email/password.
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Provider</label>
        <select value={config.provider} onChange={e => setConfig({ ...config, provider: e.target.value })} style={inputStyle}>
          <option value="oidc">OpenID Connect (OIDC)</option>
          <option value="saml">SAML 2.0</option>
          <option value="google">Google Workspace</option>
          <option value="okta">Okta</option>
          <option value="azure">Azure AD</option>
        </select>
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Client ID</label>
        <input value={config.client_id} onChange={e => setConfig({ ...config, client_id: e.target.value })} placeholder="your-client-id" style={inputStyle} />
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Client Secret</label>
        <input value={config.client_secret} onChange={e => setConfig({ ...config, client_secret: e.target.value })} placeholder="your-client-secret" type="password" style={inputStyle} />
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Issuer URL</label>
        <input value={config.issuer_url} onChange={e => setConfig({ ...config, issuer_url: e.target.value })} placeholder="https://accounts.google.com" style={inputStyle} />
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Redirect URI</label>
        <input value={config.redirect_uri} onChange={e => setConfig({ ...config, redirect_uri: e.target.value })} placeholder="http://localhost:3001/api/enterprise/sso/callback" style={inputStyle} />
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Allowed Domain (e.g. amazon.com)</label>
        <input value={config.allowed_domain} onChange={e => setConfig({ ...config, allowed_domain: e.target.value })} placeholder="yourcompany.com" style={inputStyle} />
      </div>
      <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={config.enabled} onChange={e => setConfig({ ...config, enabled: e.target.checked })} id="team-sso" />
        <label htmlFor="team-sso" style={{ fontSize: 11, color: 'var(--text-1)', fontFamily: 'var(--font-mono)', cursor: 'pointer' }}>Enable SSO for this team</label>
      </div>
      {msg && <div style={{ fontSize: 10, color: msg.startsWith('✓') ? 'var(--accent-2)' : 'var(--red)', fontFamily: 'var(--font-mono)', marginBottom: 8 }}>{msg}</div>}
      <button onClick={save} disabled={saving} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid var(--accent)', background: 'rgba(124,110,245,0.1)', color: 'var(--accent)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700 }}>
        {saving ? 'Saving...' : 'Save SSO Config'}
      </button>
    </div>
  )
}

const labelStyle: React.CSSProperties = { display: 'block', fontSize: 9, fontWeight: 700, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginBottom: 4, letterSpacing: '0.05em', textTransform: 'uppercase' }
